import { useEffect, useMemo, useState } from "react";
import { Loader2, Wallet } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { money } from "@/lib/format";
import { Dialog, DialogContent, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui-kit/Dialog";
import { Button } from "@/components/ui-kit/Button";
import { Input, Label } from "@/components/ui-kit/Input";
import { Select } from "@/components/ui-kit/Select";
import { useFunder } from "@/funder/FunderContext";

const METHODS = ["Check", "ACH", "Card", "Cash", "Retainer"];
const todayIso = () => new Date().toISOString().slice(0, 10);

/**
 * Form state lives here and (because DialogContent only mounts while open) it
 * remounts fresh every time the dialog opens — no reset effect needed.
 */
function PaymentForm({ fixedMatterId, fixedClientId, defaultPayer, lockMethod, invoiceId, onClose, onDone }) {
  const { payerLabel } = useFunder();
  const [matters, setMatters] = useState([]);
  const [matterId, setMatterId] = useState(fixedMatterId || "");
  const [payer, setPayer] = useState(defaultPayer || "Client");
  const [amount, setAmount] = useState("");
  const [date, setDate] = useState(todayIso());
  const [method, setMethod] = useState(lockMethod || "Check");
  const [reference, setReference] = useState("");
  const [note, setNote] = useState("");
  const [retainerBalance, setRetainerBalance] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    Promise.all([api.get("/matters"), api.get("/clients")]).then(([mm, cc]) => {
      const cmap = Object.fromEntries(cc.data.map((c) => [c.id, c.name]));
      const archived = new Set(cc.data.filter((c) => c.archived).map((c) => c.id));
      setMatters(mm.data.map((m) => ({ ...m, client_name: cmap[m.client_id], client_archived: archived.has(m.client_id) })));
    }).catch(() => {});
  }, []);

  const selected = useMemo(() => matters.find((m) => m.id === matterId), [matters, matterId]);
  const clientId = fixedClientId || selected?.client_id;
  const pickList = useMemo(
    () => (fixedClientId ? matters.filter((m) => m.client_id === fixedClientId) : matters.filter((m) => !m.client_archived)),
    [matters, fixedClientId]);
  const isAcsaMatter = selected?.type === "ACSA_Client";
  const isRetainer = method === "Retainer";

  useEffect(() => {
    if (isRetainer && clientId) {
      api.get(`/clients/${clientId}/retainer`).then((r) => setRetainerBalance(r.data.balance)).catch(() => setRetainerBalance(null));
    }
  }, [isRetainer, clientId]);

  const submit = async () => {
    setError("");
    const amt = Number(amount);
    if (!matterId) return setError("Select a matter.");
    if (!(amt > 0)) return setError("Enter an amount greater than 0.");
    if (isRetainer && retainerBalance != null && amt > retainerBalance + 1e-9) {
      return setError(`Cannot apply ${money(amt)} — retainer balance is only ${money(retainerBalance)}.`);
    }
    setBusy(true);
    try {
      if (isRetainer) {
        await api.post(`/clients/${clientId}/retainer/apply`, { matter_id: matterId, amount: amt, invoice_id: invoiceId, date, reference, note });
      } else {
        await api.post(`/matters/${matterId}/payments`, { payer, amount: amt, invoice_id: invoiceId, date, method, reference, notes: note });
      }
      onDone?.();
      onClose();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  return (
    <>
      <div className="mt-4 space-y-3">
        {!fixedMatterId && (
          <div>
            <Label htmlFor="pay-matter">Matter</Label>
            <Select id="pay-matter" data-testid="payment-matter-select" value={matterId} onChange={(e) => setMatterId(e.target.value)}>
              <option value="">Select a matter…</option>
              {pickList.map((m) => (
                <option key={m.id} value={m.id}>{m.client_name ? `${m.client_name} · ` : ""}{m.name}</option>
              ))}
            </Select>
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label htmlFor="pay-method">Method</Label>
            <Select id="pay-method" data-testid="payment-method-select" value={method} disabled={!!lockMethod} onChange={(e) => setMethod(e.target.value)}>
              {METHODS.map((mt) => <option key={mt} value={mt}>{mt === "Retainer" ? "Retainer (from balance)" : mt}</option>)}
            </Select>
          </div>
          <div>
            <Label htmlFor="pay-payer">Payer</Label>
            <Select id="pay-payer" data-testid="payment-payer-select" value={isRetainer ? "Client" : payer} disabled={isRetainer || !isAcsaMatter} onChange={(e) => setPayer(e.target.value)}>
              <option value="Client">{payerLabel("Client")}</option>
              {isAcsaMatter && <option value="ACSA">{payerLabel("ACSA")}</option>}
            </Select>
          </div>
        </div>

        {isRetainer && (
          <p className="text-xs text-muted-foreground" data-testid="retainer-available">
            Available retainer balance: <span className="font-medium text-foreground">{retainerBalance == null ? "…" : money(retainerBalance)}</span>
          </p>
        )}

        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label htmlFor="pay-amount">Amount ($)</Label>
            <Input id="pay-amount" data-testid="payment-amount-input" type="number" min="0" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0.00" />
          </div>
          <div>
            <Label htmlFor="pay-date">{isRetainer ? "Date applied" : "Date received"}</Label>
            <Input id="pay-date" data-testid="payment-date-input" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          </div>
        </div>

        <div>
          <Label htmlFor="pay-ref">Reference (check #, txn id)</Label>
          <Input id="pay-ref" data-testid="payment-reference-input" value={reference} onChange={(e) => setReference(e.target.value)} placeholder="Optional" />
        </div>
        <div>
          <Label htmlFor="pay-note">Note</Label>
          <Input id="pay-note" data-testid="payment-note-input" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Optional" />
        </div>

        {error && <p className="text-sm text-destructive" data-testid="payment-error">{error}</p>}
      </div>

      <DialogFooter>
        <Button variant="ghost" onClick={onClose} disabled={busy} data-testid="payment-cancel-button">Cancel</Button>
        <Button onClick={submit} disabled={busy} data-testid="payment-submit-button">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wallet className="h-4 w-4" />} {isRetainer ? "Apply retainer" : "Record payment"}
        </Button>
      </DialogFooter>
    </>
  );
}

/**
 * Record a payment for a matter. Methods Check/ACH/Card/Cash post a normal payment;
 * "Retainer" draws from the client's retainer balance (with an over-apply guard).
 * Usable with a fixed matter (workbench/invoice) or a matter picker (Payments page).
 */
export function PaymentDialog({
  open, onOpenChange, matterId, clientId, defaultPayer = "Client",
  lockMethod, invoiceId, onDone, title = "Record payment",
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="payment-dialog">
        <DialogTitle className="flex items-center gap-2">
          <span className="grid h-8 w-8 place-items-center rounded-md bg-primary/10 text-primary"><Wallet className="h-4 w-4" /></span>
          {title}
        </DialogTitle>
        <DialogDescription>Payments are recorded and never deleted — correct a mistake by voiding it.</DialogDescription>
        <PaymentForm
          fixedMatterId={matterId}
          fixedClientId={clientId}
          defaultPayer={defaultPayer}
          lockMethod={lockMethod}
          invoiceId={invoiceId}
          onClose={() => onOpenChange(false)}
          onDone={onDone}
        />
      </DialogContent>
    </Dialog>
  );
}
